import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo } from 'react'
import { EROSION_SITES, isErosionSite } from '~/constants'
import useAppStore from '~/hooks/useAppStore'
import { cdnUrl } from '~/util/cdn'
import { fetchSederoManifest } from '~/util/sederoManifest'

/**
 * Resolves a scenario id to its web COG URL for the active erosion site.
 * Returns undefined until the manifest loads, and for scenarios the manifest does not carry.
 */
export function useSederoManifest() {
  const queryClient = useQueryClient()
  const dataset = useAppStore((s) => s.dataset)
  const cosmosSite = useAppStore((s) => s.cosmosSite)
  const enabled = dataset === 'erosion' && isErosionSite(cosmosSite)

  const { data, isLoading, error } = useQuery({
    queryKey: ['sedero-manifest', cosmosSite],
    queryFn: () => fetchSederoManifest(cosmosSite),
    staleTime: Infinity,
    enabled,
  })

  useEffect(() => {
    if (!enabled) return
    for (const site of EROSION_SITES) {
      if (site === cosmosSite) continue
      void queryClient.prefetchQuery({
        queryKey: ['sedero-manifest', site],
        queryFn: () => fetchSederoManifest(site),
        staleTime: Infinity,
      })
    }
  }, [enabled, cosmosSite, queryClient])

  const urlByScenario = useMemo(() => {
    if (!data) return undefined
    return new Map(data.scenarios.map((s) => [s.scenario, cdnUrl(s.web_key)]))
  }, [data])

  return { urlByScenario, isLoading, error }
}
